import { useRef } from "react";
import type { LabeledMapSpace, MapLabels, PointOfInterest } from "./types";
import {
  MapCanvas,
  NavigationControl,
  type MapRef,
} from "./components/MapCanvas";
import { SpacePinMarker } from "./components/SpacePinMarker";
import { InfoBalloon } from "./components/InfoBalloon";
import { PoiMarkers, PoiPopup } from "./components/PoiMarkers";
import { RecenterControl } from "./components/RecenterControl";
import { NoAddressFallback } from "./components/NoAddressFallback";
import { useMappableSpaces } from "./hooks/useMappableSpaces";
import { useMapCamera } from "./hooks/useMapCamera";
import { useSpaceClusters } from "./hooks/useSpaceClusters";
import { usePoiLayer } from "./hooks/usePoiLayer";
import { useMarkerPulse } from "./hooks/useMarkerPulse";

interface SpacesLocationMapProps<S extends LabeledMapSpace> {
  center: { lat: number; lng: number };
  zoom: number;
  spaces: S[];
  pois?: PointOfInterest[];
  highlightedId: string | null;
  onSpaceClick: (id: string) => void;
  getDisplayName?: (space: S) => string;
  /** Pulses the matching marker for 2.5s (used by the ai-mode preview). */
  activeSpaceId?: string | null;
  /** Partial ok — each missing string falls back to its Portuguese default. */
  labels?: MapLabels;
}

/**
 * The standard proposal map: classic pins with info balloon, zoom + recenter
 * controls, POI layer, default Mapbox chrome. Spaces without coordinates are
 * dropped; when none can be placed, renders the no-address fallback instead
 * of mounting the map.
 */
export function SpacesLocationMap<S extends LabeledMapSpace>({
  center,
  zoom,
  spaces,
  pois = [],
  highlightedId,
  onSpaceClick,
  getDisplayName,
  activeSpaceId,
  labels,
}: SpacesLocationMapProps<S>) {
  const mapRef = useRef<MapRef>(null);
  const { mappable, noneMappable } = useMappableSpaces(spaces);
  const camera = useMapCamera({
    mapRef,
    center,
    zoom,
    highlightedId,
    spaces: mappable,
  });
  const { clusters } = useSpaceClusters(mappable, {
    mapRef,
    viewZoom: camera.viewState.zoom,
  });
  const { visiblePois, selectedPoi, selectPoi } = usePoiLayer(pois);
  const pulsingId = useMarkerPulse(activeSpaceId);

  if (noneMappable) {
    return <NoAddressFallback message={labels?.noAddress} />;
  }

  // Without pixel clustering every cluster is same-location, so a click
  // always selects the group's first member — no action routing needed.
  return (
    <MapCanvas
      mapRef={mapRef}
      viewState={camera.viewState}
      onMove={camera.onMove}
      onClick={() => selectPoi(null)}
    >
      <NavigationControl position="top-right" />
      <PoiMarkers
        pois={visiblePois}
        selectedPoi={selectedPoi}
        onSelect={selectPoi}
      />
      {selectedPoi && (
        <PoiPopup
          poi={selectedPoi}
          onClose={() => selectPoi(null)}
          noDescriptionText={labels?.poiNoDescription}
        />
      )}
      <RecenterControl
        onClick={camera.recenter}
        label={labels?.recenter}
        title={labels?.recenterTitle}
      />
      {clusters.map((cluster) => (
        <SpacePinMarker
          key={cluster.key}
          cluster={cluster}
          isHighlighted={cluster.members.some((s) => s.id === highlightedId)}
          isPulsing={cluster.members.some((s) => s.id === pulsingId)}
          onClick={() => onSpaceClick(cluster.members[0].id)}
        >
          <InfoBalloon
            isCluster={cluster.members.length > 1}
            group={cluster.members}
            representative={cluster.members[0]}
            getDisplayName={getDisplayName}
          />
        </SpacePinMarker>
      ))}
    </MapCanvas>
  );
}
